import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Play, Loader2, TrendingUp, TrendingDown, Layers } from "lucide-react";

const API = "/api";

const Backtest = () => {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState("");

  const [params, setParams] = useState({
    entry_avg_pct: 55,
    days: 7,
    direction_mode: "UP",
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadPresets = async () => {
      try {
        const r = await axios.get(`${API}/ladder-presets`, { headers });
        const all = r.data.presets || [];
        setPresets(all.filter(p => p.type === "custom"));
      } catch {}
    };
    if (token) loadPresets();
  }, [token]);

  const selectedPreset = presets.find(p => p.id === selectedPresetId);

  const runBacktest = useCallback(async () => {
    if (!selectedPresetId) {
      toast.error("Select a ladder preset first");
      return;
    }
    const preset = presets.find(p => p.id === selectedPresetId);
    if (!preset || !preset.steps?.length) {
      toast.error("Selected preset has no ladder steps");
      return;
    }

    setLoading(true);
    setResults(null);
    try {
      const payload = {
        manual_ladder: preset.steps,
        ladder_steps: preset.steps.length,
        initial_bet: preset.steps[0],
        entry_avg_pct: params.entry_avg_pct,
        days: params.days,
        direction_mode: params.direction_mode,
        cooldown_minutes: 0,
        preset_id: selectedPresetId,
      };
      const r = await axios.post(`${API}/backtest`, payload, { headers });
      setResults(r.data);
      toast.success("Backtest complete");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Backtest failed — check server logs");
    } finally {
      setLoading(false);
    }
  }, [params, selectedPresetId, presets]);

  const set = (k, v) => setParams(p => ({ ...p, [k]: v }));

  return (
    <Layout>
      <div className="space-y-5">
        <h1 className="text-[21px] font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Backtest</h1>

        {/* Preset Selector */}
        <div className="card-base">
          <div className="card-header flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-[var(--accent-teal)]" />
            <span className="label-style mb-0">Ladder Preset</span>
          </div>
          <div className="card-content">
            {presets.length === 0 ? (
              <div className="text-xs py-4 text-center" style={{ color: 'var(--text-secondary)' }}>
                No custom presets found. Create a ladder preset in <span style={{ color: 'var(--accent-teal)' }}>Settings</span> first.
              </div>
            ) : (
              <div className="space-y-1.5">
                {presets.map(p => {
                  const isActive = selectedPresetId === p.id;
                  return (
                    <button
                      key={p.id}
                      data-testid={`preset-option-${p.id}`}
                      onClick={() => setSelectedPresetId(p.id)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-all"
                      style={{
                        background: isActive ? 'rgba(0, 229, 168, 0.06)' : 'rgba(17, 24, 39, 0.5)',
                        border: `1px solid ${isActive ? 'rgba(0, 229, 168, 0.3)' : 'rgba(30, 42, 58, 0.4)'}`,
                      }}
                    >
                      <div className="w-2.5 h-2.5 rounded-full shrink-0 transition-all" style={{
                        background: isActive ? 'var(--accent-teal)' : '#1E2A3A',
                        boxShadow: isActive ? '0 0 8px rgba(0, 229, 168, 0.4)' : 'none',
                      }} />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium" style={{ color: isActive ? 'var(--accent-teal)' : 'var(--text-primary)' }}>
                          {p.name}
                        </span>
                        {p.steps && (
                          <span className="text-[10px] ml-2" style={{ color: 'var(--text-secondary)' }}>
                            {p.steps.length} steps
                          </span>
                        )}
                      </div>
                      {p.steps && (
                        <span className="text-[10px] font-mono shrink-0" style={{ color: 'var(--text-secondary)' }}>
                          ${p.steps[0]} → ${p.steps[p.steps.length - 1]}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Ladder steps preview */}
            {selectedPreset?.steps && (
              <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(30, 42, 58, 0.4)' }}>
                <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Ladder Steps
                </div>
                <div className="space-y-1">
                  {selectedPreset.steps.map((amt, i) => {
                    const cumulative = selectedPreset.steps.slice(0, i + 1).reduce((a, b) => a + (typeof b === 'number' ? b : parseFloat(b)), 0);
                    return (
                      <div key={i} className="flex items-center justify-between text-xs py-1.5" style={{ borderBottom: '1px solid rgba(30, 42, 58, 0.2)' }}>
                        <span className="font-mono w-14" style={{ color: 'var(--text-secondary)' }}>Step {i + 1}</span>
                        <div className="flex-1 mx-3">
                          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                            <div className="h-full rounded-full" style={{
                              width: `${Math.min((amt / (selectedPreset.steps[selectedPreset.steps.length - 1] || 1)) * 100, 100)}%`,
                              background: 'rgba(0, 229, 168, 0.3)',
                            }} />
                          </div>
                        </div>
                        <div className="font-mono text-right">
                          <span style={{ color: 'var(--text-primary)' }}>${typeof amt === 'number' ? amt.toFixed(2) : amt}</span>
                          <span className="text-[10px] ml-2" style={{ color: 'var(--text-secondary)' }}>(${cumulative.toFixed(2)})</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="pt-2 flex justify-between text-xs">
                  <span style={{ color: 'var(--text-secondary)' }}>Total Exposure</span>
                  <span className="font-mono" style={{ color: 'var(--accent-teal)' }}>
                    ${selectedPreset.steps.reduce((a, b) => a + (typeof b === 'number' ? b : parseFloat(b)), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Parameters */}
        <div className="card-base">
          <div className="card-header">
            <span className="label-style mb-0">Parameters</span>
          </div>
          <div className="card-content">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="label-style">Avg Entry %</label>
                <input data-testid="backtest-entry-avg" type="number" min="1" max="99" step="1"
                  value={params.entry_avg_pct}
                  onChange={e => set("entry_avg_pct", parseFloat(e.target.value) || "")}
                  className="input-base w-full px-3" />
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Contract price you typically enter at
                </div>
              </div>
              <div>
                <label className="label-style">Days</label>
                <input data-testid="backtest-days" type="number" min="1" max="90" step="1"
                  value={params.days}
                  onChange={e => set("days", parseInt(e.target.value) || "")}
                  className="input-base w-full px-3" />
              </div>
              <div>
                <label className="label-style">Direction</label>
                <select data-testid="backtest-direction" value={params.direction_mode}
                  onChange={e => set("direction_mode", e.target.value)}
                  className="input-base w-full px-3">
                  <option value="UP">UP Only</option>
                  <option value="DOWN">DOWN Only</option>
                </select>
              </div>
            </div>
            <button data-testid="run-backtest-btn" onClick={runBacktest} disabled={loading || !selectedPresetId}
              className="btn-primary mt-5 inline-flex items-center gap-2 disabled:opacity-40" style={{ color: '#0B0F1A' }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {loading ? "Running..." : "Run Backtest"}
            </button>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4 animate-fade-in">
            {/* Summary bar */}
            <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono px-1" style={{ color: 'var(--text-secondary)' }}>
              <span>Entry: {results.entry_used || params.entry_avg_pct}%</span>
              <span>|</span>
              <span>Direction: {results.direction_used || params.direction_mode}</span>
              <span>|</span>
              <span>{results.total_trades} / {results.windows_analyzed} windows traded</span>
              {results.data_coverage != null && results.data_coverage < 95 && (
                <>
                  <span>|</span>
                  <span style={{ color: 'var(--accent-yellow)' }}>Data coverage: {results.data_coverage}%</span>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className={`card-base p-4 ${(results.total_profit ?? 0) >= 0 ? "stat-teal" : "stat-red"}`}>
                <div className="label-style">Total P/L</div>
                <div className="stat-number" style={{ color: (results.total_profit ?? 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                  {(results.total_profit ?? 0) >= 0 ? "+" : ""}${(results.total_profit ?? 0).toFixed(2)}
                </div>
              </div>
              <div className="card-base p-4 stat-cyan">
                <div className="label-style">Win Rate</div>
                <div className="stat-number" style={{ color: 'var(--text-primary)' }}>{(results.win_rate || 0).toFixed(1)}%</div>
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--accent-teal)' }}>{results.wins || 0}W</span>{" / "}
                  <span style={{ color: 'var(--accent-red)' }}>{results.losses || 0}L</span>
                </div>
              </div>
              <div className="card-base p-4 stat-cyan">
                <div className="label-style">Total Trades</div>
                <div className="stat-number" style={{ color: 'var(--text-primary)' }}>{results.total_trades || 0}</div>
              </div>
              <div className="card-base p-4 stat-yellow">
                <div className="label-style">Max Drawdown</div>
                <div className="stat-number" style={{ color: 'var(--accent-red)' }}>
                  -${Math.abs(results.max_drawdown || 0).toFixed(2)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="card-base p-4">
                <div className="label-style">Avg Daily P/L</div>
                <div className="text-sm font-bold font-mono" style={{ color: (results.avg_daily_profit || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                  {(results.avg_daily_profit || 0) >= 0 ? "+" : ""}${(results.avg_daily_profit || 0).toFixed(2)}
                </div>
              </div>
              <div className="card-base p-4">
                <div className="label-style">Ladder Failures</div>
                <div className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{results.ladder_failures || 0}</div>
              </div>
              <div className="card-base p-4">
                <div className="label-style">Best Day</div>
                <div className="text-sm font-bold font-mono" style={{ color: 'var(--accent-teal)' }}>+${(results.best_day || 0).toFixed(2)}</div>
              </div>
              <div className="card-base p-4">
                <div className="label-style">Worst Day</div>
                <div className="text-sm font-bold font-mono" style={{ color: 'var(--accent-red)' }}>${(results.worst_day || 0).toFixed(2)}</div>
              </div>
            </div>

            {/* Trade list */}
            {results.trades?.length > 0 && (
              <div className="card-base">
                <div className="card-header flex items-center justify-between">
                  <span className="label-style mb-0">Trades</span>
                  <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>{results.trades.length} trades</span>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  <div className="table-header grid grid-cols-6 gap-2 px-4 py-3">
                    {["Time", "BTC Move", "Result", "Step", "Bet", "P/L"].map(h => (
                      <div key={h}>{h}</div>
                    ))}
                  </div>
                  {results.trades.map((t, i) => (
                    <div key={i} className="grid grid-cols-6 gap-2 px-4 py-2.5 table-row text-[13px]">
                      <div style={{ color: 'var(--text-secondary)' }}>{t.time || "--"}</div>
                      <div className="flex items-center gap-1">
                        {t.actual === "UP" ? <TrendingUp className="w-3 h-3" style={{ color: 'var(--accent-teal)' }} />
                          : <TrendingDown className="w-3 h-3" style={{ color: 'var(--accent-red)' }} />}
                        <span style={{ color: t.actual === "UP" ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                          {t.btc_change ? `${t.btc_change > 0 ? "+" : ""}${t.btc_change.toFixed(3)}%` : t.actual}
                        </span>
                      </div>
                      <div className="font-bold" style={{ color: t.result === "WIN" ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                        {t.result}
                      </div>
                      <div style={{ color: 'var(--text-secondary)' }} className="font-mono">{t.step}</div>
                      <div style={{ color: 'var(--text-primary)' }} className="font-mono">${(t.bet || 0).toFixed(2)}</div>
                      <div className="font-bold font-mono" style={{ color: (t.pnl || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                        {(t.pnl || 0) >= 0 ? "+" : ""}${(t.pnl || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily Results */}
            {results.daily_results?.length > 0 && (
              <div className="card-base">
                <div className="card-header flex items-center justify-between">
                  <span className="label-style mb-0">Daily Breakdown</span>
                  <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>{results.daily_results.length} days</span>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  <div className="table-header grid grid-cols-4 gap-2 px-4 py-3">
                    {["Date", "Trades", "Day P/L", "Running P/L"].map(h => (
                      <div key={h}>{h}</div>
                    ))}
                  </div>
                  {results.daily_results.map((d, i) => (
                    <div key={i} className="grid grid-cols-4 gap-2 px-4 py-2.5 table-row text-[13px]">
                      <div style={{ color: 'var(--text-secondary)' }}>{d.date}</div>
                      <div style={{ color: 'var(--text-primary)' }}>{d.trades}</div>
                      <div className="font-mono font-bold" style={{ color: (d.pnl || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                        {(d.pnl || 0) >= 0 ? "+" : ""}${(d.pnl || 0).toFixed(2)}
                      </div>
                      <div className="font-mono" style={{ color: (d.running_pnl || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                        {(d.running_pnl || 0) >= 0 ? "+" : ""}${(d.running_pnl || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Backtest;
